import { clsx } from 'clsx';
import { X, Edit3 } from 'lucide-react';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { RedFlagCard } from './RedFlagCard';
import { RagnarokSources } from './RagnarokSources';
import { normalizeRedFlags } from '../../lib/redFlags';
import type { Evaluation, PredefinedRedFlag } from '../../types';

// Minimal framework shape needed for display
interface FrameworkForDisplay {
  sections: {
    id: string;
    name: string;
    weight: number;
    criteria: {
      id: string;
      name: string;
    }[];
  }[];
}

interface EvaluationBreakdownProps {
  /** The evaluation to display */
  evaluation: Evaluation;
  /** Display label (e.g., 'YOUR', 'AI', evaluator name) */
  label: string;
  /** Framework for displaying scores by section */
  framework?: FrameworkForDisplay;
  /** Red flag definitions for normalization */
  redFlagDefinitions: PredefinedRedFlag[];
  /** Whether to show Edit button at bottom (only for YOUR evaluation) */
  showEditButton?: boolean;
  /** Callback when Edit is clicked */
  onEdit?: () => void;
  /** Callback when Close button is clicked */
  onClose: () => void;
}

export function EvaluationBreakdown({
  evaluation,
  label,
  framework,
  redFlagDefinitions,
  showEditButton = false,
  onEdit,
  onClose,
}: EvaluationBreakdownProps) {
  const normalizedFlags = normalizeRedFlags(evaluation.red_flags, redFlagDefinitions);
  const isAI = evaluation.is_ai;

  return (
    <div role="region" aria-live="polite">
      <Card className="mt-6">
        {/* Header with close button */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-wolf-700/50">
        <div className="flex items-center gap-2">
          <span className="text-action font-mono text-sm">{label}</span>
          <span className="text-wolf-300 font-medium">Evaluation</span>
        </div>
        <button
          onClick={onClose}
          className="text-wolf-400 hover:text-wolf-100 transition-colors"
          aria-label="Close breakdown"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-6">
        {/* Red flags */}
        {normalizedFlags.length > 0 && (
          <div>
            <h4 className="text-red-400 font-medium mb-3 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              Red Flags ({normalizedFlags.length})
            </h4>
            <div className="space-y-2">
              {normalizedFlags.map((flag) => (
                <RedFlagCard key={flag.id} flag={flag} />
              ))}
            </div>
          </div>
        )}

        {/* AI-specific: Summary narrative */}
        {isAI && evaluation.summary?.narrative && (
          <div>
            <h4 className="text-wolf-300 font-medium mb-2">Summary</h4>
            <p className="text-wolf-400 text-sm whitespace-pre-wrap">
              {evaluation.summary.narrative}
            </p>
          </div>
        )}

        {/* AI-specific: Strengths and gaps */}
        {isAI && (evaluation.summary?.strengths || evaluation.summary?.gaps) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {evaluation.summary?.strengths && (
              <div className="bg-action/10 border border-action/30 p-4">
                <h4 className="text-action font-medium mb-2">Strengths</h4>
                <p className="text-wolf-300 text-sm">{evaluation.summary.strengths}</p>
              </div>
            )}
            {evaluation.summary?.gaps && (
              <div className="bg-amber-500/10 border border-amber-500/30 p-4">
                <h4 className="text-amber-400 font-medium mb-2">Gaps</h4>
                <p className="text-wolf-300 text-sm">{evaluation.summary.gaps}</p>
              </div>
            )}
          </div>
        )}

        {/* AI-specific: Recommendation */}
        {isAI && evaluation.summary?.recommendation && (
          <div className="bg-wolf-800/50 p-4">
            <h4 className="text-wolf-300 font-medium mb-2">Recommendation</h4>
            <p className="text-wolf-400 text-sm">{evaluation.summary.recommendation}</p>
          </div>
        )}

        {/* AI-specific: Ragnarok company knowledge sources */}
        {isAI && <RagnarokSources ragnarokContext={evaluation.ragnarok_context} />}

        {/* Detailed scores by section */}
        <div>
          <h4 className="text-wolf-300 font-medium mb-4">Detailed Scores</h4>
          <div className="space-y-4">
            {framework?.sections.map((section) => (
              <div key={section.id} className="border border-wolf-700/50 p-4">
                <h5 className="text-wolf-100 font-medium mb-3">
                  {section.id}. {section.name}
                  <span className="text-wolf-500 font-normal ml-2">
                    ({(section.weight * 100).toFixed(0)}% weight)
                  </span>
                </h5>
                <div className="space-y-3">
                  {section.criteria.map((criterion) => {
                    const score = evaluation.scores[criterion.id];
                    if (!score) return null;
                    return (
                      <div key={criterion.id} className="flex gap-4">
                        <div className="flex-shrink-0 w-12">
                          <span
                            className={clsx(
                              'font-mono text-lg font-bold',
                              getIndividualScoreColor(score.score)
                            )}
                          >
                            {score.score}
                          </span>
                          <span className="text-wolf-500 text-xs">/5</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-wolf-100 text-sm">{criterion.name}</div>
                          {score.rationale && (
                            <div className="text-wolf-400 text-xs mt-1">{score.rationale}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {/* Show scores without framework context */}
            {!framework && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(evaluation.scores).map(([id, score]) => (
                  <div key={id} className="flex gap-3 bg-wolf-800/30 p-3">
                    <div className="flex-shrink-0">
                      <span
                        className={clsx(
                          'font-mono text-lg font-bold',
                          getIndividualScoreColor(score.score)
                        )}
                      >
                        {score.score}
                      </span>
                      <span className="text-wolf-500 text-xs">/5</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-wolf-100 text-sm font-mono">{id}</div>
                      {score.rationale && (
                        <div className="text-wolf-400 text-xs mt-1 truncate">{score.rationale}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Edit button footer (YOUR evaluation only) */}
        {showEditButton && (
          <div className="pt-4 border-t border-wolf-700/50">
            <Button variant="secondary" size="sm" onClick={onEdit} className="w-full">
              <Edit3 className="w-4 h-4 mr-2" />
              Edit Evaluation
            </Button>
          </div>
        )}
      </div>
      </Card>
    </div>
  );
}

function getIndividualScoreColor(score: number): string {
  if (score >= 4) return 'text-action';
  if (score >= 3) return 'text-amber-400';
  if (score >= 2) return 'text-orange-400';
  return 'text-red-400';
}
